import { Menu, X, Bell, User, Store, ChevronDown, LogOut, LayoutDashboard, ShoppingBag, ListOrdered, ShieldAlert, ShieldCheck, MessageSquare, Settings, Coins, PlusCircle, ArrowRight, Copy, Check } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { m, AnimatePresence, LazyMotion, domAnimation } from 'framer-motion';
import { useLocale, SUPPORTED_LOCALES } from '@/lib/i18n';
import { useSectionTranslation } from '@/hooks';
import { useAuth } from '@/lib/auth/AuthContext';
import { api, isApiError } from '@/lib/api';
import { formatPrice } from '@/lib/formatPrice';
import { logger } from '@/lib/logger';
import { Avatar } from '@/components/Avatar';
import { headerTranslations } from './translations';
import { SearchAutocomplete } from './SearchAutocomplete';
import { CurrencySelector } from './CurrencySelector';
import { API_ORIGIN } from '@/lib/api-origin';
import {
  formatRelativeTime,
  interpolate,
  notificationIcon,
  notificationRoute,
  type NotificationItem,
} from './notificationUtils';

interface WalletBalance {
  currency: string;
  availableBalance: string;
  pendingBalance: string;
  escrowBalance: string;
}

const navLinks = [
  { key: 'home', href: '/' },
  { key: 'browse', href: '/browse' },
  { key: 'giveaways', href: '/giveaways', icon: true },
  { key: 'sellers', href: '/seller/activate' },
  { key: 'support', href: '/support' },
] as const;

export function Header() {
  const { locale, direction, setLocale } = useLocale();
  const t = useSectionTranslation(headerTranslations);
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [localeMenuOpen, setLocaleMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsError, setNotificationsError] = useState(false);
  const [balances, setBalances] = useState<WalletBalance[]>([]);
  const [isSeller, setIsSeller] = useState(false);
  const [sellerStatusLoaded, setSellerStatusLoaded] = useState(false);
  const [accountIdCopied, setAccountIdCopied] = useState(false);
  const copyTimerRef = useRef<number | null>(null);
  const localeRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const isRTL = direction === 'rtl';
  const primaryBalance = balances[0];

  useEffect(() => {
    if (!user) { setBalances([]); setIsSeller(false); setSellerStatusLoaded(false); return; }
    api.get<{ balances: WalletBalance[] }>('/wallet').then((res) => {
      if (!isApiError(res)) setBalances(res.data.balances ?? []);
    });
    setSellerStatusLoaded(false);
    api.get<{ isSeller: boolean }>('/seller/status')
      .then((res) => {
        if (!isApiError(res)) setIsSeller(res.data.isSeller);
      })
      .finally(() => setSellerStatusLoaded(true));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const socket = io(API_ORIGIN, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });
    socket.on('notification.created', (n: NotificationItem) => {
      setNotifications((prev) => [n, ...prev].slice(0, 30));
      setUnreadCount((c) => c + 1);
    });
    return () => {
      socket.off('notification.created');
      socket.disconnect();
    };
  }, [user]);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setNotificationsError(false);
      return;
    }
    api.get<{ items: NotificationItem[]; unreadCount: number }>('/notifications')
      .then((res) => {
        if (isApiError(res)) {
          setNotificationsError(true);
          return;
        }
        setNotifications(res.data.items ?? []);
        setUnreadCount(res.data.unreadCount ?? 0);
        setNotificationsError(false);
      })
      .catch(() => setNotificationsError(true));
  }, [user]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (localeRef.current && !localeRef.current.contains(e.target as Node)) {
        setLocaleMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target as Node)) {
        setNotificationsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(
    () => () => {
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
    },
    [],
  );

  const copyAccountId = async () => {
    const id = user?.userId;
    if (!id) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(id);
      } else {
        const ta = document.createElement('textarea');
        ta.value = id;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setAccountIdCopied(true);
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => setAccountIdCopied(false), 2000);
    } catch {
      /* clipboard unavailable — leave button unchanged */
    }
  };

  const handleNavClick = (href: string) => {
    logger.info('Nav clicked', { href, locale });
  };

  const handleLocaleSelect = (next: 'en-US' | 'ar-SA') => {
    setLocale(next);
    setLocaleMenuOpen(false);
  };

  const handleNotificationClick = async (n: NotificationItem) => {
    if (n.readAt == null) {
      setNotifications((prev) =>
        prev.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      const res = await api.post<{ ok: boolean }>('/notifications/read', { id: n.id });
      if (isApiError(res)) {
        setNotifications((prev) =>
          prev.map((x) => (x.id === n.id ? { ...x, readAt: null } : x)),
        );
        setUnreadCount((c) => c + 1);
      }
    }
    setNotificationsOpen(false);
    navigate(notificationRoute(n.type));
  };

  const handleMarkAllRead = async () => {
    const res = await api.post<{ ok: boolean }>('/notifications/read-all');
    if (isApiError(res)) return;
    setNotifications((prev) =>
      prev.map((x) => (x.readAt == null ? { ...x, readAt: new Date().toISOString() } : x)),
    );
    setUnreadCount(0);
  };

  return (
    <LazyMotion features={domAnimation}>
      <header className="fixed top-0 left-0 right-0 z-50 bg-surface-card border-b border-surface-border">
        <div className="container-main">
          <div className="flex h-16 items-center gap-4">
            <Link to="/" className="flex items-center shrink-0 group" aria-label={t.brand}>
              <span className="font-display text-xl font-bold tracking-tight text-brand-white group-hover:text-accent transition-colors">
                {t.brand}
              </span>
            </Link>

            <div className="hidden md:flex flex-1 max-w-xl mx-auto">
              <SearchAutocomplete className="flex w-full relative" />
            </div>

            <nav className="hidden xl:flex items-center gap-6" aria-label="Main navigation">
              {navLinks.map((link) => (
                <Link
                  key={link.key}
                  to={link.href}
                  className="text-sm font-medium text-brand-muted hover:text-accent transition-colors"
                >
                  {'icon' in link && link.icon ? (
                    <img
                      src="/prizes.png"
                      alt=""
                      aria-hidden="true"
                      width={16}
                      height={16}
                      className="inline-block size-4 object-contain me-1.5 -mt-0.5"
                    />
                  ) : null}
                  {t.nav[link.key]}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-2 md:gap-3 ms-auto">
              <div className="relative hidden md:block" ref={localeRef}>
                <button
                  type="button"
                  onClick={() => setLocaleMenuOpen((v) => !v)}
                  aria-label={t.locale.label}
                  className="flex items-center gap-1.5 h-9 px-2 rounded-lg border border-surface-border text-sm hover:border-accent/30 transition-colors"
                >
                  <span className={`fi fi-${locale === 'en-US' ? 'us' : 'sa'} rounded-sm`} />
                  <ChevronDown size={14} className={`text-brand-muted transition-transform ${localeMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {localeMenuOpen && (
                    <m.div
                      initial={{ opacity: 0, y: 4, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 4, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full mt-2 min-w-[140px] rounded-xl bg-surface-card border border-surface-border shadow-card overflow-hidden"
                      style={{ [isRTL ? 'left' : 'right']: 0 }}
                    >
                      {SUPPORTED_LOCALES.map((entry) => (
                        <button
                          key={entry.locale}
                          type="button"
                          onClick={() => handleLocaleSelect(entry.locale)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-brand-white hover:bg-surface-hover transition-colors"
                        >
                          <span className={`fi fi-${entry.locale === 'en-US' ? 'us' : 'sa'} rounded-sm`} />
                          {entry.label}
                        </button>
                      ))}
                    </m.div>
                  )}
                </AnimatePresence>
              </div>

              <CurrencySelector className="relative hidden md:block" />

              {user ? (
                <Link
                  to="/dashboard/messages"
                  aria-label={t.messages.label}
                  title={t.messages.label}
                  className="h-9 w-9 flex items-center justify-center rounded-lg border border-surface-border text-brand-muted hover:text-brand-white hover:border-accent/30 transition-colors"
                >
                  <MessageSquare size={16} />
                </Link>
              ) : null}

              <div className="relative" ref={notificationsRef}>
                <button
                  type="button"
                  onClick={() => setNotificationsOpen((v) => !v)}
                  aria-label={t.notifications.label}
                  className="relative h-9 w-9 flex items-center justify-center rounded-lg border border-surface-border text-brand-muted hover:text-brand-white hover:border-accent/30 transition-colors"
                >
                  <Bell size={16} />
                  {unreadCount > 0 ? (
                    <span
                      aria-live="polite"
                      className="absolute -top-1 -end-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-surface-base"
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  ) : null}
                </button>
                <AnimatePresence>
                  {notificationsOpen && (
                    <m.div
                      initial={{ opacity: 0, y: 4, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 4, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                      className="fixed inset-x-2 top-[4.25rem] z-50 max-h-[calc(100dvh-5.5rem)] overflow-y-auto rounded-xl bg-surface-card border border-surface-border shadow-card sm:absolute sm:inset-x-auto sm:top-full sm:mt-2 sm:end-0 sm:max-h-[calc(100dvh-5rem)] sm:w-[min(280px,calc(100vw-1.5rem))]"
                    >
                      <div className="px-4 py-3 border-b border-surface-border">
                        <p className="text-sm font-semibold text-brand-white">{t.notifications.label}</p>
                      </div>
                      {notificationsError ? (
                        <div className="px-4 py-8 text-center text-sm text-brand-danger">
                          {t.notifications.loadError}
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-brand-muted">
                          {t.notifications.empty}
                        </div>
                      ) : (
                        <>
                          <ul className="divide-y divide-surface-border max-h-[320px] overflow-y-auto">
                            {notifications.map((n) => {
                              const Icon = notificationIcon(n.type);
                              const title = interpolate(t.notifications.types[n.type].title, n.data);
                              const body = interpolate(t.notifications.types[n.type].body, n.data);
                              return (
                                <li key={n.id}>
                                  <button
                                    type="button"
                                    onClick={() => handleNotificationClick(n)}
                                    className="w-full flex items-start gap-3 px-4 py-3 text-start hover:bg-surface-hover transition-colors"
                                  >
                                    <span
                                      className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                                        n.readAt == null
                                          ? 'bg-accent/15 text-accent'
                                          : 'bg-surface-hover text-brand-muted'
                                      }`}
                                    >
                                      <Icon size={14} />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                      <span
                                        className={`block text-sm ${
                                          n.readAt == null ? 'font-medium text-brand-white' : 'text-brand-muted'
                                        }`}
                                      >
                                        {title}
                                      </span>
                                      <span className="block text-xs text-brand-muted mt-0.5 truncate">{body}</span>
                                      <span className="block text-[11px] text-brand-muted/70 mt-1">
                                        {formatRelativeTime(n.createdAt, locale)}
                                      </span>
                                    </span>
                                    {n.readAt == null ? (
                                      <span
                                        className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent"
                                        aria-hidden="true"
                                      />
                                    ) : null}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                          <div className="px-4 py-2.5 border-t border-surface-border">
                            <button
                              type="button"
                              onClick={handleMarkAllRead}
                              className="w-full text-xs font-medium text-accent hover:text-brand-white transition-colors"
                            >
                              {t.notifications.markAllRead}
                            </button>
                          </div>
                        </>
                      )}
                    </m.div>
                  )}
                </AnimatePresence>
              </div>

              {user ? (
                <div className="relative" ref={profileRef}>
                  <button
                    type="button"
                    onClick={() => setProfileMenuOpen((v) => !v)}
                    className="flex items-center gap-2 h-9 px-3 rounded-lg border border-surface-border hover:border-accent/30 transition-colors"
                  >
                    <div className="relative shrink-0">
                      <Avatar src={user.avatarUrl} name={user.displayName} size="xs" />
                      <span className="absolute -bottom-px -right-px h-2 w-2 rounded-full bg-brand-live border-2 border-surface-base" />
                    </div>
                    <span className="hidden xl:inline text-sm font-medium text-brand-white max-w-[100px] truncate">
                      {user.displayName}
                    </span>
                  </button>
                  <AnimatePresence>
                    {profileMenuOpen && (
                      <m.div
                        initial={{ opacity: 0, y: 4, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 4, scale: 0.97 }}
                        transition={{ duration: 0.18, ease: 'easeOut' }}
                        className="fixed inset-x-2 top-[4.25rem] z-50 max-h-[calc(100dvh-5.5rem)] overflow-y-auto rounded-2xl bg-surface-card border border-surface-border shadow-lift sm:absolute sm:inset-x-auto sm:top-full sm:mt-2 sm:end-0 sm:max-h-[calc(100dvh-5rem)] sm:w-[min(300px,calc(100vw-1.5rem))]"
                      >
                        <div className="px-4 pt-4 pb-3 bg-surface-hover/40">
                          <div className="flex items-center gap-3">
                            <div className="relative shrink-0">
                              <Avatar src={user.avatarUrl} name={user.displayName} size="md" />
                              <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-brand-live border-2 border-surface-hover" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-brand-white truncate">{user.displayName}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <p className="text-[11px] text-brand-muted truncate">
                                  {t.profile.accountId}: {user.userId.slice(0, 8)}&hellip;
                                </p>
                                <button
                                  type="button"
                                  onClick={copyAccountId}
                                  aria-label={accountIdCopied ? t.profile.accountIdCopied : t.profile.copyAccountId}
                                  title={accountIdCopied ? t.profile.accountIdCopied : t.profile.copyAccountId}
                                  className="shrink-0 flex items-center justify-center h-5 w-5 rounded text-brand-muted hover:text-brand-white transition-colors"
                                >
                                  {accountIdCopied ? <Check size={12} className="text-brand-success" /> : <Copy size={12} />}
                                </button>
                                {accountIdCopied && (
                                  <span className="text-[11px] text-brand-success" aria-live="polite">
                                    {t.profile.accountIdCopied}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="px-4 py-3 space-y-2.5">
                          {!primaryBalance ? (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-sm text-brand-muted">
                                <Coins size={14} className="text-brand-success" />
                                <span>{t.profile.availableBalance}</span>
                              </div>
                              <span className="text-sm font-semibold text-brand-white">
                                {formatPrice('0', 'USDT', locale)}
                              </span>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-brand-muted">
                                  <Coins size={14} className="text-brand-success" />
                                  <span>{t.profile.availableBalance}</span>
                                </div>
                                <span className="text-sm font-semibold text-brand-white">
                                  {formatPrice(primaryBalance.availableBalance, primaryBalance.currency, locale)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-brand-muted">
                                  <Coins size={14} className="text-accent" />
                                  <span>{t.profile.pendingBalance}</span>
                                </div>
                                <span className="text-sm text-brand-muted">
                                  {formatPrice(primaryBalance.pendingBalance, primaryBalance.currency, locale)}
                                </span>
                              </div>
                            </>
                          )}
                        </div>

                        {isSeller ? (
                          <div className="px-4 pb-1">
                            <Link to="/seller/listings/new" className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-gradient-to-r from-accent/15 to-accent/5 border border-accent/10 hover:border-accent/25 transition-all group">
                              <div className="flex items-center gap-2.5">
                                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-accent/20 text-accent">
                                  <PlusCircle size={14} />
                                </div>
                                <span className="text-xs font-medium text-brand-white">{t.seller.addProduct}</span>
                              </div>
                              <ArrowRight size={13} className="text-accent group-hover:translate-x-0.5 transition-transform" />
                            </Link>
                          </div>
                        ) : null}

                        <div className="mx-4 my-2 h-px bg-surface-border" />

                        <div className="py-1 max-h-[260px] overflow-y-auto">
                          {[
                            { icon: LayoutDashboard, label: t.profile.dashboard, href: '/dashboard' },
                            { icon: ShoppingBag, label: t.profile.myOrders, href: '/dashboard/orders' },
                            { icon: MessageSquare, label: t.profile.messages, href: '/dashboard/messages' },
                            { icon: ListOrdered, label: t.profile.myListings, href: '/dashboard/listings' },
                            ...(isSeller
                              ? [{ icon: Store, label: t.profile.sellerDashboard, href: '/seller/dashboard' }]
                              : []),
                            { icon: ShieldAlert, label: t.profile.supportTickets, href: '/support' },
                            { icon: Settings, label: t.profile.settings, href: '/settings' },
                            ...(user?.roles?.includes('admin')
                              ? [{ icon: ShieldCheck, label: t.profile.adminPanel, href: '/admin' }]
                              : []),
                          ].map((item) => (
                            <Link
                              key={item.label}
                              to={item.href}
                              onClick={() => setProfileMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-2 text-sm text-brand-muted hover:text-brand-white hover:bg-surface-hover transition-colors"
                            >
                              <item.icon size={16} className="shrink-0" />
                              <span className="flex-1">{item.label}</span>
                            </Link>
                          ))}
                        </div>

                        <div className="h-px bg-surface-border" />
                        <button
                          type="button"
                          onClick={async () => {
                            setProfileMenuOpen(false);
                            await logout();
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-brand-danger hover:bg-brand-danger/5 transition-colors"
                        >
                          <LogOut size={16} />
                          {t.profile.signOut}
                        </button>
                      </m.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : loading ? (
                <span className="h-9 w-24 rounded-lg bg-surface-card border border-surface-border animate-pulse" />
              ) : (
                <Link to="/auth/signin" className="btn-secondary h-9">
                  <User size={16} />
                  <span className="hidden sm:inline">{t.auth.signIn}</span>
                </Link>
              )}

              {loading ? null : !sellerStatusLoaded ? (
                <span className="hidden md:block h-9 w-28 rounded-lg border border-surface-border bg-surface-card animate-pulse" />
              ) : isSeller ? (
                <Link to="/seller/listings/new" className="btn-primary h-9 hidden md:flex">
                  <PlusCircle size={16} />
                  {t.cta.sellNow}
                </Link>
              ) : (
                <Link to="/seller/activate" className="btn-primary h-9 hidden md:flex">
                  <Store size={16} />
                  {t.cta.sell}
                </Link>
              )}
            </div>

            <button
              type="button"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-label={mobileMenuOpen ? t.menu.close : t.menu.open}
              aria-expanded={mobileMenuOpen}
              className="xl:hidden h-10 w-10 flex items-center justify-center rounded-lg border border-surface-border text-brand-white hover:border-accent/30 transition-colors"
            >
              {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <m.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className="xl:hidden border-t border-surface-border bg-surface-card overflow-hidden"
            >
              <div className="container-main py-6 space-y-4">
                <SearchAutocomplete
                  className="flex w-full relative"
                  dropdownMode="overlay"
                  onNavigate={() => setMobileMenuOpen(false)}
                />

                <nav className="flex flex-col gap-1" aria-label="Mobile navigation">
                  {navLinks.map((link) => (
                    <Link
                      key={link.key}
                      to={link.href}
                      onClick={() => { handleNavClick(link.href); setMobileMenuOpen(false); }}
                      className="h-11 flex items-center gap-2.5 px-3 rounded-lg text-base font-medium text-brand-muted hover:text-brand-white hover:bg-surface-hover transition-colors"
                    >
                      {'icon' in link && link.icon ? (
                        <img
                          src="/prizes.png"
                          alt=""
                          aria-hidden="true"
                          width={20}
                          height={20}
                          className="size-5 object-contain shrink-0"
                        />
                      ) : null}
                      {t.nav[link.key]}
                    </Link>
                  ))}
                  {user?.roles?.includes('admin') ? (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="h-11 flex items-center gap-2.5 px-3 rounded-lg text-base font-medium text-accent hover:bg-surface-hover transition-colors"
                    >
                      <ShieldCheck size={20} className="shrink-0" />
                      {t.profile.adminPanel}
                    </Link>
                  ) : null}
                </nav>

                <div className="pt-4 border-t border-surface-border flex flex-col gap-3">
                  <div className="flex gap-2">
                    {SUPPORTED_LOCALES.map((entry) => (
                      <button
                        key={entry.locale}
                        type="button"
                        onClick={() => { handleLocaleSelect(entry.locale); setMobileMenuOpen(false); }}
                        className={`flex-1 h-11 flex items-center justify-center gap-2 rounded-lg border text-sm font-medium transition-colors ${
                          locale === entry.locale
                            ? 'border-accent/30 bg-accent/10 text-accent'
                            : 'border-surface-border text-brand-muted hover:text-brand-white hover:border-accent/30'
                        }`}
                      >
                        <span className={`fi fi-${entry.locale === 'en-US' ? 'us' : 'sa'} rounded-sm`} />
                        {entry.label}
                      </button>
                    ))}
                  </div>

                  <CurrencySelector className="relative" />

                  {!sellerStatusLoaded ? null : isSeller ? (
                    <Link to="/seller/listings/new" className="btn-primary h-11" onClick={() => setMobileMenuOpen(false)}>
                      <PlusCircle size={18} />
                      {t.cta.sellNow}
                    </Link>
                  ) : (
                    <Link to="/seller/activate" className="btn-primary h-11" onClick={() => setMobileMenuOpen(false)}>
                      <Store size={18} />
                      {t.cta.sell}
                    </Link>
                  )}
                </div>
              </div>
            </m.div>
          )}
        </AnimatePresence>
      </header>
    </LazyMotion>
  );
}
